import React, { useState } from 'react';
import { ProjectStatus, ProjectPhaseDetail } from '../types';
import { useUser } from '../contexts/UserContext';
import ProjectPhasesEditor from './ProjectPhasesEditor';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ProjectPhasesDetail from './ProjectPhasesDetail';
import EditIcon from '@mui/icons-material/Edit';

interface ProjectStatusSectionProps {
  projectStatus: ProjectStatus;
  ultimateGoal?: string;
  clientId: string;
  onUpdate: (updatedProjectStatus: ProjectStatus) => void;
}

const ProjectStatusSection: React.FC<ProjectStatusSectionProps> = ({ 
  projectStatus, 
  ultimateGoal, 
  clientId, 
  onUpdate 
}) => {
  const [showUltimateGoal, setShowUltimateGoal] = useState(false);
  const [showDetailedPhases, setShowDetailedPhases] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useUser();

  // Fetch user permissions
  const toggleUltimateGoal = () => {
    setShowUltimateGoal(!showUltimateGoal);
  };

  const toggleDetailedPhases = () => {
    setShowDetailedPhases(!showDetailedPhases);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleEditorClose = () => {
    setIsEditing(false);
  };

  const handleEditorSave = (updatedPhases: ProjectPhaseDetail[], updatedOverallStatus?: string) => {
    // Update the project status with new phases
    const updatedProjectStatus: ProjectStatus = {
      ...projectStatus,
      allPhases: updatedPhases,
      overallStatus: updatedOverallStatus || ""
    };

    // update updatedProjectStatus.currentPhase with the element in allPhases that has the same name
    const currentPhase = updatedPhases.find(phase => phase.name === projectStatus.currentPhase.name);
    if (currentPhase) {
      updatedProjectStatus.currentPhase = {
        ...projectStatus.currentPhase,
        ...currentPhase
      };
    }

    onUpdate(updatedProjectStatus);
    setIsEditing(false);
  };

  return (
    <>
      <section id="project-status" className="mb-16">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-satoshi font-bold text-3xl m-0 flex items-center gap-3">
            <PushPinOutlinedIcon className="text-3xl" />
            Project Status
          </h2>
          {user?.canEdit ? <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick();
              }}
              style={{
                background: 'transparent',
                color: '#3b82f6',
                border: '1px solid #bfdbfe',
                borderRadius: 8,
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#eff6ff';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
            >
              <EditIcon style={{ fontSize: 14 }} />
              Edit
            </button>
          </div> : null}
        </div>

        <div className="space-y-6">
          {/* Overall Status */}
          <div>
            <h3 className="font-satoshi font-bold text-lg mb-2">Overall Status</h3>
            <div className="flex items-center gap-2">
              <SentimentSatisfiedAltOutlinedIcon />
              <span className="font-satoshi text-lg">{projectStatus.overallStatus}</span>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Current Phase & Progress */}
          {!showDetailedPhases && (
            <div>
              <h3 className="font-satoshi font-bold text-lg mb-4">Current Phase & Progress</h3>
              <div className="mb-4">
                <div className="font-satoshi text-lg mb-2">
                  <span className="font-bold italic">Phase {projectStatus.currentPhase.current}/{projectStatus.currentPhase.total}</span>
                  <span className="mx-2">—</span>
                  <span className="text-black">{projectStatus.currentPhase.description} · {projectStatus.currentPhase.targetDate}</span>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gray-200 rounded-full h-3 flex-1">
                    <div 
                      className="h-3 rounded-full transition-all duration-300" 
                      style={{ 
                        width: `${projectStatus.currentPhase.progress}%`,
                        backgroundColor: 'rgba(89, 221, 254, 1)'
                      }}
                    ></div>
                  </div>
                  <span className="font-bold text-lg">{projectStatus.currentPhase.progress}%</span>
                </div>
              </div>

              <button 
                onClick={toggleDetailedPhases}
                className="bg-blue-50 text-blue-600 border-none rounded-full px-3 py-1 text-lg font-medium cursor-pointer hover:border-blue-200 transition-all border border-transparent"
                title="See more details"
              >
                See more details
              </button>
            </div>
          )}

          {showDetailedPhases && projectStatus.allPhases && (
            <div>
              <h3 className="font-satoshi font-bold text-lg mb-4">Current Phase & Progress</h3>
              <ProjectPhasesDetail phases={projectStatus.allPhases} />
              <button 
                onClick={toggleDetailedPhases}
                className="bg-blue-50 text-blue-600 border-none rounded-full px-3 py-1 text-lg font-medium cursor-pointer hover:border-blue-200 transition-all border border-transparent mt-4"
                title="See less details"
              >
                See less details
              </button>
            </div>
          )}

          <hr className="border-gray-200" />

          {/* Milestone */}
          {projectStatus.currentPhase.milestone && (
            <div>
              <h3 className="font-satoshi font-bold text-lg mb-2">Milestone</h3>
              <p className="font-satoshi text-lg">{projectStatus.currentPhase.milestone}</p>
              
              <button 
                onClick={toggleUltimateGoal}
                className="bg-blue-50 text-blue-600 border-none rounded-full px-3 py-1 text-lg font-medium cursor-pointer hover:border-blue-200 transition-all border border-transparent mt-4"
                title={showUltimateGoal ? 'Hide ultimate goal' : 'See ultimate goal'}
              >
                {showUltimateGoal ? 'Hide ultimate goal' : 'See ultimate goal'}
              </button>
            </div>
          )}

          {/* Ultimate Goal Section */}
          {ultimateGoal && showUltimateGoal && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-satoshi font-bold text-lg mb-2">Ultimate Goal</h3>
              <p className="font-satoshi text-base leading-relaxed">{ultimateGoal}</p>
            </div>
          )}
        </div>
      </section>
      
      <ProjectPhasesEditor
        isOpen={isEditing}
        onClose={handleEditorClose}
        clientId={clientId}
        initialOverallStatus={projectStatus.overallStatus}
        initialPhases={projectStatus.allPhases || []}
        onSave={handleEditorSave}
      />
    </>
  );
};

export default ProjectStatusSection; 